///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../../../project-view-panel/logical-topology/entities/position';
import { TopologyNodeFeatureDTO } from './topology-types.model';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { NodeTypeEnum, SectorPolarityEnum, TopologyAltitudeModeEnum } from '../../../generated/shared-types.model';

export class GtthNodeFeatureDtoModel implements TopologyNodeFeatureDTO {
    public static readonly DEFAULT_POLE_BLOCKED_ANGLE = -1;
    public static readonly POLE_BLOCKED_NO = 0;
    public static readonly DEFAULT_BU_DEVICE_AZIMUTH = -1;
    public static readonly PREDEFINED_PRODUCT_NOT_DEFINED = 0;

    i: number;
    n: string;
    h: number;
    rm: number;
    p: number;
    en: number;
    bl: number;
    mh: NodeTypeEnum;
    la: number;
    lo: number;
    al: number;
    li: number;
    da: number;
    dp: SectorPolarityEnum;
    bmp: number;

    public static fromData(data: TopologyNodeFeatureDTO): GtthNodeFeatureDtoModel {
        const clearedPosition = GtthPosition.fromValues(data.lo, data.la);

        const node = new GtthNodeFeatureDtoModel();
        node.i = data.i;
        node.n = data.n;
        node.la = clearedPosition.latitude;
        node.lo = clearedPosition.longitude;
        node.h = data.h;
        node.al = data.al;
        node.mh = data.mh;
        node.li = data.li;
        node.da = data.da;
        node.dp = data.dp;

        node.rm = data.rm;
        node.p = data.p;
        node.en = data.en;
        node.bl = data.bl;
        node.bmp = data.bmp;

        return node;
    }

    public static fromPosition(
        position: GtthPosition,
        defaultHeight: number,
        defaultAltMode: TopologyAltitudeModeEnum,
        activeLayer: number,
        blockedByPole: number
    ): GtthNodeFeatureDtoModel {
        const node = new GtthNodeFeatureDtoModel();
        node.setId(null);
        node.setName(null);
        node.setLat(position.latitude);
        node.setLng(position.longitude);
        node.setHeight(defaultHeight);
        node.setAltMode(defaultAltMode);
        node.setMhType(NodeTypeEnum.BEST_EFFORT);
        node.setLayerIndex(activeLayer);
        node.setBuDeviceAzimuth(GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH);
        node.setBuDevicePolarity(SectorPolarityEnum.UNSPECIFIED);
        node.setPredefinedBuProductId(0);

        node.setToRemove(false);
        node.setIsPop(false);
        node.setIsEnabled(true);
        node.setBlockedByPoleAngle(blockedByPole);
        return node;
    }

    public equals(node: GtthNodeFeatureDtoModel): boolean {
        return (
            this.n === node.n &&
            this.la === node.la &&
            this.lo === node.lo &&
            this.h === node.h &&
            this.al === node.al &&
            this.getIsPop() === node.getIsPop() &&
            this.getIsEnabled() === node.getIsEnabled() &&
            this.mh === node.mh &&
            this.li === node.li &&
            this.bl === node.bl &&
            this.da === node.da &&
            this.dp === node.dp &&
            this.bmp === node.bmp
        );
    }

    public getId(): number {
        return this.i;
    }

    public setId(id: number): void {
        this.i = id;
    }

    public getName(): string {
        return this.n;
    }

    public setName(value: string): void {
        this.n = value;
    }

    public getLat(): number {
        return this.la;
    }

    public setLat(la: number): void {
        this.la = la;
    }

    public getLng(): number {
        return this.lo;
    }

    public setLng(lo: number): void {
        this.lo = lo;
    }

    public getHeight(): number {
        return this.h;
    }

    public setHeight(value: number): void {
        this.h = value;
    }

    public getToRemove(): boolean {
        return this.rm !== undefined;
    }

    public setToRemove(value: boolean): void {
        this.rm = value ? 1 : undefined;
    }

    public getAltMode(): TopologyAltitudeModeEnum {
        return this.al === undefined ? TopologyAltitudeModeEnum.RELATIVE : TopologyAltitudeModeEnum.ABSOLUTE;
    }

    public setAltMode(al: TopologyAltitudeModeEnum) {
        this.al = al === TopologyAltitudeModeEnum.RELATIVE ? undefined : al;
    }

    public getIsPop(): boolean {
        return this.p !== undefined;
    }

    public setIsPop(value: boolean): void {
        this.p = value ? 1 : undefined;
    }

    public getIsEnabled(): boolean {
        return this.en === undefined;
    }

    public setIsEnabled(value: boolean): void {
        this.en = value ? undefined : 0;
    }

    public getMhType(): NodeTypeEnum {
        return this.mh ?? NodeTypeEnum.BEST_EFFORT;
    }

    public setMhType(type: NodeTypeEnum): void {
        this.mh = type === NodeTypeEnum.BEST_EFFORT ? undefined : type;
    }

    public getLayerIndex(): number {
        return this.li ?? GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    }

    public setLayerIndex(index: number): void {
        this.li = index === GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX ? undefined : index;
    }

    public getBlockedByPoleAngle(): number {
        return this.bl ?? GtthNodeFeatureDtoModel.POLE_BLOCKED_NO;
    }

    public setBlockedByPoleAngle(value: number): void {
        this.bl = value === GtthNodeFeatureDtoModel.POLE_BLOCKED_NO ? undefined : value;
    }

    public getBuDeviceAzimuth(): number {
        return this.da ?? GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH;
    }

    public setBuDeviceAzimuth(value: number): void {
        this.da = value === GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH ? undefined : value;
    }

    public getBuDevicePolarity(): SectorPolarityEnum {
        return this.dp ?? SectorPolarityEnum.UNSPECIFIED;
    }

    public setBuDevicePolarity(value: SectorPolarityEnum): void {
        this.dp = value === SectorPolarityEnum.UNSPECIFIED ? undefined : value;
    }

    public getPredefinedBuProductId(): number {
        return this.bmp ?? GtthNodeFeatureDtoModel.PREDEFINED_PRODUCT_NOT_DEFINED;
    }

    public setPredefinedBuProductId(value: number): void {
        this.bmp = value === GtthNodeFeatureDtoModel.PREDEFINED_PRODUCT_NOT_DEFINED ? undefined : value;
    }
}
